import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  customersSidebar,
  financeSidebar,
  mainNavItems,
  marketingSidebar,
  moreMenuItems,
  ordersSidebar,
  productsSidebar,
  salesChannelsSidebar,
  settingsSidebar,
  type NavSection,
} from '@/config/navigation';
import { type PermissionString } from '@/lib/constants/permissions';
import { useRouter } from '@/lib/compat/navigation';
import { useFilteredNavSections } from '@/lib/hooks/use-filtered-nav-sections';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useLazyQuery } from '@/lib/hooks/use-query';
import { GET_CUSTOMERS } from '@/lib/graphql/operations/customers';
import { GET_ORDERS_FILTERED } from '@/lib/graphql/operations/orders';
import { SEARCH_PRODUCTS } from '@/lib/graphql/operations/products';
import { GET_CATEGORIES } from '@/lib/graphql/operations/categories';
import { GET_COLLECTIONS } from '@/lib/graphql/operations/collections';
import { useTranslations } from '@/lib/i18n/client';
import { useI18nSafe } from '@/lib/i18n/i18n-context';
import { buildOrderListSearchVariables } from '@/lib/utils/order-list-search';
import {
  getGlobalSearchGroupOrder,
  normalizeGlobalSearchText,
  type GlobalSearchResultKind,
} from '@/lib/utils/global-search';
import {
  ArrowDown,
  ArrowUp,
  BadgePercent,
  Boxes,
  CornerDownLeft,
  FolderTree,
  LoaderCircle,
  PackagePlus,
  PackageSearch,
  Search,
  ShoppingBag,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface QuickAction {
  href: string;
  icon: LucideIcon;
  labelKey: string;
  requiredPermission: PermissionString;
}

interface ProductSearchResult {
  productId: string;
  productName: string;
  productVariantId: string;
  productVariantName: string;
  sku: string;
}

interface OrderSearchResult {
  id: string;
  code: string;
  customer?: {
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
  } | null;
}

interface CustomerSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string | null;
}

interface CategorySearchResult {
  id: string;
  name: string;
  slug: string;
  productCount?: number | null;
  parent?: { name?: string | null } | null;
  breadcrumbs?: Array<{ name: string }> | null;
}

interface CollectionSearchResult {
  id: string;
  name: string;
  slug: string;
  parent?: { name?: string | null } | null;
  productVariants?: { totalItems: number } | null;
}

interface RecordSearchResults {
  products: ProductSearchResult[];
  orders: OrderSearchResult[];
  customers: CustomerSearchResult[];
  categories: CategorySearchResult[];
  collections: CollectionSearchResult[];
}

const emptyRecordResults: RecordSearchResults = {
  products: [],
  orders: [],
  customers: [],
  categories: [],
  collections: [],
};

const quickActions: QuickAction[] = [
  {
    href: '/products/new',
    icon: PackagePlus,
    labelKey: 'navigation.commandMenu.createProduct',
    requiredPermission: 'CreateCatalog',
  },
  {
    href: '/orders/new',
    icon: ShoppingBag,
    labelKey: 'navigation.commandMenu.createOrder',
    requiredPermission: 'CreateOrder',
  },
  {
    href: '/marketing/discounts/new',
    icon: BadgePercent,
    labelKey: 'navigation.commandMenu.createDiscount',
    requiredPermission: 'CreatePromotion',
  },
];

const primaryDestinationHrefs = new Set(['/products', '/orders', '/customers']);

const commandSections: NavSection[] = [
  {
    titleKey: 'navigation.quickNavigation',
    items: [...mainNavItems, ...moreMenuItems],
  },
  ...productsSidebar,
  ...ordersSidebar,
  ...customersSidebar,
  ...marketingSidebar,
  ...salesChannelsSidebar,
  ...financeSidebar,
  ...settingsSidebar,
];

function commandFilter(value: string, search: string, keywords?: string[]): number {
  const normalizedSearch = normalizeGlobalSearchText(search);
  if (!normalizedSearch) return 1;
  const candidate = normalizeGlobalSearchText([value, ...(keywords ?? [])].join(' '));
  return candidate.includes(normalizedSearch) ? 1 : 0;
}

interface GlobalCommandMenuProps {
  triggerVariant?: 'header' | 'icon';
}

/** One global, keyboard-accessible jump menu replaces nested header navigation. */
export function GlobalCommandMenu({ triggerVariant = 'header' }: GlobalCommandMenuProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordResults, setRecordResults] = useState<RecordSearchResults>(emptyRecordResults);
  const searchRequestId = useRef(0);
  const router = useRouter();
  const t = useTranslations();
  const { language } = useI18nSafe();
  const { hasPermission } = usePermissions();
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const visibleSections = useFilteredNavSections(commandSections) ?? [];
  const canSearchProducts = hasPermission('ReadCatalog');
  const canSearchTaxonomy = hasPermission('ReadCatalog');
  const canSearchOrders = hasPermission('ReadOrder');
  const canSearchCustomers = hasPermission('ReadCustomer');

  const { execute: searchProducts, loading: productsLoading } = useLazyQuery<
    { search: { items: ProductSearchResult[] } },
    { input: { term: string; take: number; groupByProduct: boolean } }
  >(SEARCH_PRODUCTS, { requestPolicy: 'network-only' });
  const { execute: searchOrders, loading: ordersLoading } = useLazyQuery<
    { ordersFiltered: { items: OrderSearchResult[] } },
    Record<string, unknown>
  >(GET_ORDERS_FILTERED, { requestPolicy: 'network-only' });
  const { execute: searchCustomers, loading: customersLoading } = useLazyQuery<
    { customers: { items: CustomerSearchResult[] } },
    { options: Record<string, unknown> }
  >(GET_CUSTOMERS, { requestPolicy: 'network-only' });
  const { execute: searchCategories, loading: categoriesLoading } = useLazyQuery<
    { categories: { items: CategorySearchResult[] } },
    { options: Record<string, unknown> }
  >(GET_CATEGORIES, { requestPolicy: 'network-only' });
  const { execute: searchCollections, loading: collectionsLoading } = useLazyQuery<
    { collections: { items: CollectionSearchResult[] } },
    { options: Record<string, unknown> }
  >(GET_COLLECTIONS, { requestPolicy: 'network-only' });

  const visibleQuickActions = useMemo(
    () => quickActions.filter((action) => hasPermission(action.requiredPermission)),
    [hasPermission],
  );

  const sections = useMemo(() => {
    const seen = new Set<string>();
    return visibleSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (seen.has(item.href)) return false;
          seen.add(item.href);
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [visibleSections]);

  const primaryDestinations = useMemo(() => {
    const byHref = new Map(
      sections.flatMap((section) => section.items).map((item) => [item.href, item]),
    );

    return [...primaryDestinationHrefs]
      .map((href) => byHref.get(href))
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [sections]);

  useEffect(() => {
    const term = debouncedSearch.trim();
    const requestId = ++searchRequestId.current;

    if (!open || term.length < 2) {
      setRecordResults(emptyRecordResults);
      return;
    }

    const orderSearch = buildOrderListSearchVariables(term);
    const orderFilter: Record<string, unknown> = {};
    if (orderSearch.orderCode) {
      orderFilter.code = { contains: orderSearch.orderCode };
    }
    const orderVariables: Record<string, unknown> = {
      options: {
        take: 5,
        sort: { createdAt: 'DESC' },
        ...(Object.keys(orderFilter).length > 0 ? { filter: orderFilter } : {}),
      },
      ...orderSearch,
    };
    delete orderVariables.orderCode;

    void Promise.all([
      canSearchProducts
        ? searchProducts({ input: { term, take: 5, groupByProduct: true } })
        : Promise.resolve({ data: null, error: null }),
      canSearchOrders
        ? searchOrders(orderVariables)
        : Promise.resolve({ data: null, error: null }),
      canSearchCustomers
        ? searchCustomers({
            options: {
              take: 5,
              sort: { createdAt: 'DESC' },
              filter: {
                _or: [
                  { firstName: { contains: term } },
                  { lastName: { contains: term } },
                  { emailAddress: { contains: term } },
                  { phoneNumber: { contains: term } },
                ],
              },
            },
          })
        : Promise.resolve({ data: null, error: null }),
      canSearchTaxonomy
        ? searchCategories({
            options: {
              take: 5,
              sort: { name: 'ASC' },
              filter: { name: { contains: term } },
            },
          })
        : Promise.resolve({ data: null, error: null }),
      canSearchTaxonomy
        ? searchCollections({
            options: {
              take: 5,
              sort: { name: 'ASC' },
              filter: { name: { contains: term } },
            },
          })
        : Promise.resolve({ data: null, error: null }),
    ]).then(([productResult, orderResult, customerResult, categoryResult, collectionResult]) => {
      if (requestId !== searchRequestId.current) return;
      setRecordResults({
        products: productResult.data?.search?.items ?? [],
        orders: orderResult.data?.ordersFiltered?.items ?? [],
        customers: customerResult.data?.customers?.items ?? [],
        categories: categoryResult.data?.categories?.items ?? [],
        collections: collectionResult.data?.collections?.items ?? [],
      });
    });
  }, [
    canSearchCustomers,
    canSearchOrders,
    canSearchProducts,
    canSearchTaxonomy,
    debouncedSearch,
    open,
    searchCustomers,
    searchCategories,
    searchCollections,
    searchOrders,
    searchProducts,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const goTo = (href: string) => {
    setOpen(false);
    setSearchValue('');
    window.requestAnimationFrame(() => {
      void router.push(href);
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      searchRequestId.current += 1;
      setSearchValue('');
      setRecordResults(emptyRecordResults);
    }
  };

  const showSearchResults = searchValue.trim().length > 0;
  const isSearchingRecords = searchValue.trim().length >= 2
    && (productsLoading || ordersLoading || customersLoading || categoriesLoading || collectionsLoading);
  const hasRecordResults = recordResults.products.length > 0
    || recordResults.orders.length > 0
    || recordResults.customers.length > 0
    || recordResults.categories.length > 0
    || recordResults.collections.length > 0;
  const resultGroupOrder = getGlobalSearchGroupOrder(searchValue);

  const renderItem = (
    item: { href: string; icon: LucideIcon; labelKey: string },
    options?: { showPath?: boolean },
  ) => {
    const Icon = item.icon;
    return (
      <CommandItem
        key={item.href}
        value={`${t(item.labelKey)} ${item.href}`}
        onSelect={() => goTo(item.href)}
        className="group min-h-14 rounded-xl px-4 text-base data-[selected=true]:bg-accent sm:min-h-16 sm:text-lg"
      >
        <Icon className="text-muted-foreground group-data-[selected=true]:text-foreground" />
        <span className="min-w-0 flex-1 truncate font-medium">{t(item.labelKey)}</span>
        {options?.showPath ? (
          <span className="hidden text-xs text-muted-foreground sm:inline" dir="ltr">
            {item.href}
          </span>
        ) : null}
        <CornerDownLeft
          aria-hidden="true"
          className="text-muted-foreground opacity-0 transition-opacity group-data-[selected=true]:opacity-100"
        />
      </CommandItem>
    );
  };

  const renderRecordGroup = (kind: GlobalSearchResultKind) => {
    const groupClassName = '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-sm';

    if (kind === 'products' && recordResults.products.length > 0) {
      return (
        <CommandGroup key={kind} forceMount heading={t('navigation.products')} className={groupClassName}>
          {recordResults.products.map((product) => (
            <CommandItem
              key={`product-${product.productId}`}
              value={`${product.productName} ${product.productVariantName} ${product.sku}`}
              keywords={[t('navigation.products')]}
              onSelect={() => goTo(`/products/${product.productId}`)}
              className="group min-h-14 rounded-xl px-4 text-base data-[selected=true]:bg-accent sm:min-h-16"
            >
              <PackageSearch className="text-muted-foreground group-data-[selected=true]:text-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{product.productName}</span>
                <span className="block truncate text-xs text-muted-foreground" dir="ltr">
                  {product.sku || product.productVariantName}
                </span>
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      );
    }

    if (kind === 'orders' && recordResults.orders.length > 0) {
      return (
        <CommandGroup key={kind} forceMount heading={t('navigation.orders')} className={groupClassName}>
          {recordResults.orders.map((order) => {
            const customerName = [order.customer?.firstName, order.customer?.lastName]
              .filter(Boolean)
              .join(' ');
            return (
              <CommandItem
                key={`order-${order.id}`}
                value={`${order.code} ${customerName} ${order.customer?.emailAddress ?? ''}`}
                keywords={[t('navigation.orders')]}
                onSelect={() => goTo(`/orders/${order.id}`)}
                className="group min-h-14 rounded-xl px-4 text-base data-[selected=true]:bg-accent sm:min-h-16"
              >
                <ShoppingBag className="text-muted-foreground group-data-[selected=true]:text-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium" dir="ltr">#{order.code}</span>
                  {customerName || order.customer?.emailAddress ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {customerName || order.customer?.emailAddress}
                    </span>
                  ) : null}
                </span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      );
    }

    if (kind === 'customers' && recordResults.customers.length > 0) {
      return (
        <CommandGroup key={kind} forceMount heading={t('navigation.customers')} className={groupClassName}>
          {recordResults.customers.map((customer) => (
            <CommandItem
              key={`customer-${customer.id}`}
              value={`${customer.firstName} ${customer.lastName} ${customer.emailAddress} ${customer.phoneNumber ?? ''}`}
              keywords={[t('navigation.customers')]}
              onSelect={() => goTo(`/customers/${customer.id}`)}
              className="group min-h-14 rounded-xl px-4 text-base data-[selected=true]:bg-accent sm:min-h-16"
            >
              <UserRound className="text-muted-foreground group-data-[selected=true]:text-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
                </span>
                <span className="block truncate text-xs text-muted-foreground" dir="ltr">
                  {customer.emailAddress || customer.phoneNumber}
                </span>
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      );
    }

    if (kind === 'categories' && recordResults.categories.length > 0) {
      return (
        <CommandGroup key={kind} forceMount heading={t('navigation.categories')} className={groupClassName}>
          {recordResults.categories.map((category) => {
            const breadcrumb = category.breadcrumbs?.map((item) => item.name).join(' / ')
              || category.parent?.name
              || category.slug;
            return (
              <CommandItem
                key={`category-${category.id}`}
                value={`${category.name} ${category.slug} ${breadcrumb}`}
                keywords={[t('navigation.categories')]}
                onSelect={() => goTo(`/categories/${category.id}`)}
                className="group min-h-14 rounded-xl px-4 text-base data-[selected=true]:bg-accent sm:min-h-16"
              >
                <FolderTree className="text-muted-foreground group-data-[selected=true]:text-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{category.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{breadcrumb}</span>
                </span>
                {typeof category.productCount === 'number' ? (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {category.productCount} {t('navigation.products')}
                  </span>
                ) : null}
              </CommandItem>
            );
          })}
        </CommandGroup>
      );
    }

    if (kind === 'collections' && recordResults.collections.length > 0) {
      return (
        <CommandGroup key={kind} forceMount heading={t('navigation.collections')} className={groupClassName}>
          {recordResults.collections.map((collection) => (
            <CommandItem
              key={`collection-${collection.id}`}
              value={`${collection.name} ${collection.slug} ${collection.parent?.name ?? ''}`}
              keywords={[t('navigation.collections')]}
              onSelect={() => goTo(`/collections/${collection.id}`)}
              className="group min-h-14 rounded-xl px-4 text-base data-[selected=true]:bg-accent sm:min-h-16"
            >
              <Boxes className="text-muted-foreground group-data-[selected=true]:text-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{collection.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {collection.parent?.name || collection.slug}
                </span>
              </span>
              {typeof collection.productVariants?.totalItems === 'number' ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {collection.productVariants.totalItems} {t('navigation.products')}
                </span>
              ) : null}
            </CommandItem>
          ))}
        </CommandGroup>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={triggerVariant === 'icon' ? 'icon' : 'default'}
          className={triggerVariant === 'icon'
            ? 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
            : 'h-9 gap-2 border-border bg-card px-2.5 text-muted-foreground hover:bg-accent hover:text-foreground lg:min-w-56 lg:justify-start'}
          aria-label={t('navigation.jumpTo')}
        >
          <Search />
          {triggerVariant === 'header' ? (
            <>
              <span className="hidden flex-1 text-start text-sm font-normal lg:inline">
                {t('navigation.jumpTo')}
              </span>
              <kbd className="hidden rounded border border-border bg-background px-1.5 py-0.5 font-sans text-[10px] text-muted-foreground xl:inline">
                ⌘K
              </kbd>
            </>
          ) : null}
        </Button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-foreground/20 backdrop-blur-sm"
        className="h-[calc(100svh-1rem)] max-h-[46rem] gap-0 overflow-hidden rounded-2xl border-border bg-card p-0 sm:h-[min(44rem,calc(100svh-3rem))] sm:max-w-3xl"
        dir={direction}
      >
        <DialogTitle className="sr-only">{t('navigation.jumpTo')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('navigation.commandMenu.description')}
        </DialogDescription>
        <Command
          className="rounded-2xl [&_[data-slot=command-input-wrapper]]:h-20 [&_[data-slot=command-input-wrapper]]:gap-3 [&_[data-slot=command-input-wrapper]]:px-5 [&_[data-slot=command-input-wrapper]_svg]:size-6 sm:[&_[data-slot=command-input-wrapper]]:h-24 sm:[&_[data-slot=command-input-wrapper]]:px-7"
          dir={direction}
          filter={commandFilter}
          loop
        >
          <CommandInput
            autoFocus
            value={searchValue}
            onValueChange={setSearchValue}
            placeholder={t('navigation.commandMenu.searchPlaceholder')}
            className="h-full text-lg sm:text-2xl"
            dir={direction}
          />
          <CommandList className="min-h-0 max-h-none flex-1 p-3 sm:p-5">
            {/* Record groups are force-mounted so the server's match survives cmdk's
                local substring filter. cmdk propagates forceMount to the items inside
                and skips registering them, so they never reach the `filtered.count`
                that CommandEmpty keys off — leaving "no matching results" sitting
                above real results. Suppress it explicitly whenever records are on
                screen; cmdk's own count still covers the navigation items, which are
                registered normally. */}
            {hasRecordResults ? null : (
              <CommandEmpty className="py-16 text-muted-foreground">
                {isSearchingRecords
                  ? t('navigation.commandMenu.searching')
                  : t('navigation.commandMenu.noResults')}
              </CommandEmpty>
            )}
            {showSearchResults && isSearchingRecords ? (
              <CommandGroup
                forceMount
                heading={t('navigation.commandMenu.results')}
                className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-1 [&_[cmdk-group-heading]]:text-sm"
              >
                <div className="flex min-h-14 items-center gap-3 px-4 text-sm text-muted-foreground">
                  <LoaderCircle className="animate-spin" />
                  {t('navigation.commandMenu.searching')}
                </div>
              </CommandGroup>
            ) : null}
            {showSearchResults && hasRecordResults
              ? resultGroupOrder.map(renderRecordGroup)
              : null}
            <CommandGroup
              heading={t('navigation.commandMenu.quickActions')}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-1 [&_[cmdk-group-heading]]:text-sm"
            >
              {visibleQuickActions.map((action) => renderItem(action))}
            </CommandGroup>

            {!showSearchResults && primaryDestinations.length > 0 ? (
              <CommandGroup
                heading={t('navigation.commandMenu.goTo')}
                className="mt-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-sm"
              >
                {primaryDestinations.map((item) => renderItem(item))}
              </CommandGroup>
            ) : null}

            {showSearchResults
              ? sections.map((section, sectionIndex) => (
                  <CommandGroup
                    key={`${section.titleKey ?? 'section'}-${sectionIndex}`}
                    heading={section.titleKey ? t(section.titleKey) : undefined}
                    className="mt-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-sm"
                  >
                    {section.items.map((item) => renderItem(item, { showPath: true }))}
                  </CommandGroup>
                ))
              : null}
          </CommandList>
          <Separator />
          <div
            className="hidden min-h-14 items-center gap-5 px-6 text-xs text-muted-foreground sm:flex"
            aria-hidden="true"
          >
            <span className="flex items-center gap-2">
              {t('navigation.commandMenu.navigate')}
              <kbd className="inline-flex rounded-md border bg-background p-1.5"><ArrowUp /></kbd>
              <kbd className="inline-flex rounded-md border bg-background p-1.5"><ArrowDown /></kbd>
            </span>
            <span className="flex items-center gap-2">
              {t('navigation.commandMenu.select')}
              <kbd className="inline-flex rounded-md border bg-background p-1.5"><CornerDownLeft /></kbd>
            </span>
            <span className="flex items-center gap-2">
              {t('navigation.commandMenu.close')}
              <kbd className="rounded-md border bg-background px-2 py-1 font-sans" dir="ltr">Esc</kbd>
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
